import React from 'react';
import { useMetricFormatter } from 'src/utils/Hooks';
import { ValueOf } from 'sl-api-connector/types';
import { METRICTYPE } from '../../../utils/entityDefinitions';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { shouldShowNewAtlas } from 'src/utils/app';

interface MetricCellProps {
  value: number;
  metricType: ValueOf<typeof METRICTYPE>;
  percentChange?: number;
  /** When true (e.g. Ad Spend, CPC, CPA), use neutral grey for percent change (matches key metric cards) */
  isAdSpendMetric?: boolean;
  comparisonNode?: React.ReactNode;
  wrapperStyle?: React.CSSProperties;
}

/**
 * Shows a metric and optionally a percent change for use
 * in a table
 */
const MetricCell = ({
  value,
  metricType,
  percentChange,
  isAdSpendMetric = false,
  comparisonNode,
  wrapperStyle
}: MetricCellProps) => {
  const formatMetric = useMetricFormatter();

  let changeColor: 'success' | 'error' | 'secondary' | undefined;
  if (percentChange === undefined) {
    changeColor = undefined;
  } else if (isAdSpendMetric || percentChange === 0) {
    changeColor = 'secondary';
  } else {
    changeColor = percentChange > 0 ? 'success' : 'error';
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '0px',
        gap: '1px',
        alignItems: 'flex-start',
        justifyContent: percentChange === undefined ? 'center' : 'flex-start',
        height: '100%',
        boxSizing: 'border-box',
        ...wrapperStyle
      }}
    >
      <Text variant="body2">
        {formatMetric(value, metricType, {
          decimalPlaces:
            metricType === METRICTYPE.PERCENT || (shouldShowNewAtlas() && metricType === METRICTYPE.DECIMAL) ? 1 : 0
        })}
      </Text>
      {percentChange !== undefined && changeColor && (
        <Text variant="body2" color={changeColor}>
          {percentChange >= 0 ? '+' : ''}
          {formatMetric(percentChange, METRICTYPE.PERCENT, { decimalPlaces: 1 })}
        </Text>
      )}
      {comparisonNode || null}
    </div>
  );
};

export default MetricCell;
